import type { UseQueryResult } from '@tanstack/react-query';
import { useBasicQuery } from '@hooks/requests/utils/use_basic_query';
import { keysToCamel } from '@lib/case_converter';
import { PARTNER_ORGANIZATIONS_PATH } from '@hooks/requests/utils/paths';

// ── Types ──────────────────────────────────────────────────────────────────────

/** Raw wire shape returned by GET /api/v1/partner/organizations (snake_case). */
interface PartnerOrganizationResponse {
  organization_id: string;
  organization_name: string | null;
  /** ISO 8601 timestamp; null for legacy orgs created before this field existed. */
  organization_created_at: string | null;
}

export interface PartnerOrganization {
  organizationId: string;
  organizationName: string | null;
  /** ISO 8601 timestamp; null for legacy orgs created before this field existed. */
  organizationCreatedAt: string | null;
}

// ── Hook ───────────────────────────────────────────────────────────────────────

/**
 * Fetches all organizations the caller has access to.
 *
 * @param enabled  Pass `false` to suppress the call (e.g. for non-AceUp-staff roles).
 */
export const usePartnerOrganizationsQuery = (
  enabled: boolean
): UseQueryResult<PartnerOrganization[], Error> =>
  useBasicQuery<PartnerOrganizationResponse[], PartnerOrganization[]>(
    PARTNER_ORGANIZATIONS_PATH,
    undefined,
    {
      enabled,
      // keysToCamel deep-converts every key in each org object (snake_case ->
      // camelCase); it's untyped at the boundary, hence the cast back to
      // PartnerOrganization[].
      select: (raw) => keysToCamel(raw) as PartnerOrganization[],
    }
  );
